/* eslint-disable import/no-extraneous-dependencies */
import { Grid } from '@mui/material';
import { BasicButton } from '@tassat/button';
import { DatePicker, SimpleErrorMessage, TextField } from '@tassat/formik-input';
import { ActionTypes } from '@tassat/ui-definitions';
import { Formik, FormikProps } from 'formik';
import moment from 'moment';
import * as React from 'react';

import { validateApiConnection } from './submission';
import { IFormProps, IFormValues, IFormikProps } from './types';

const AUDIENCE_PREFIX = 'https://';

const FormContents = (props: IFormProps) => {
    const {
        classes,
        values,
        errors,
        submitForm,
        isValid,
        isSubmitting,
        dirty,
        formProps,
        submitError
    } = props;

    const {
        actionType,
        longField = true
    } = formProps;

    return (
        <form className={classes.flexColumn}>
            <Grid
                container
                direction='column'
                spacing={0}
            >
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={11}
                        sm={12}
                    >
                        <TextField
                            errors={errors}
                            label='Connection Name'
                            maxLength={longField ? 64 : 32}
                            name='APIConnectionName'
                            placeholder='Api Connection Name'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            errors={errors}
                            label='Contact Name'
                            maxLength={32}
                            name='APIFirmContactName'
                            placeholder='Contact Name'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            errors={errors}
                            label='Support Phone'
                            maxLength={32}
                            name='APIFirmPhone'
                            placeholder='Phone Number'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    justifyContent='center'
                    sm={12}
                >
                    <Grid
                        item
                        md={11}
                        sm={12}
                    >
                        <TextField
                            errors={errors}
                            label='Technical Notifications Email'
                            maxLength={64}
                            name='APIFirmEmail'
                            placeholder='Email'
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    justifyContent='center'
                    sm={12}
                >
                    <Grid
                        item
                        md={11}
                        sm={12}
                    >
                        <TextField
                            disabled={actionType === ActionTypes.edit}
                            errors={errors}
                            label='Audience'
                            maxLength={64 - AUDIENCE_PREFIX.length}
                            name='Audience'
                            placeholder={AUDIENCE_PREFIX}
                            values={values}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    direction='row'
                    item
                    justifyContent='space-around'
                    sm={12}
                >
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <TextField
                            disabled={actionType === ActionTypes.edit}
                            errors={errors}
                            label='Source ID'
                            maxLength={32}
                            name='SourceId'
                            placeholder='Source ID'
                            values={values}
                        />
                    </Grid>
                    <Grid
                        item
                        md={5}
                        sm={12}
                    >
                        <DatePicker
                            errors={errors}
                            label='Effective As of Date'
                            name='EffectiveDate'
                            placeholder='Select a Date'
                        />
                    </Grid>
                </Grid>
            </Grid>
            <BasicButton
                className={classes.submitButton}
                disabled={!dirty ||
                    isSubmitting ||
                    !isValid}
                onClick={submitForm}
                text='Submit'
                type='submit'
            />
            <SimpleErrorMessage
                className={classes.submitError}
                error={submitError}
            />
        </form>
    );
};

export default function AddApiConnectionForm(props: IFormikProps) {
    const {
        classes,
        onClose,
        formProps
    } = props;

    const {
        actionType,
        apiConnection,
        apiConnections,
        submitApiConnection
    } = formProps;

    const [ submitError, setSubmitError ] = React.useState<string>('');

    let initialValues = {} as IFormValues;
    if (actionType === ActionTypes.add) {
        initialValues = {
            APIConnectionName: '',
            Audience: '',
            SourceId: '',
            APIFirmContactName: '',
            APIFirmEmail: '',
            APIFirmPhone: '',
            EffectiveDate: moment()
                .format('YYYY-MM-DD')
        };
    } else if (actionType === ActionTypes.edit) {
        if (!apiConnection) return <div />;

        const EffectiveDate = moment(apiConnection.EffectiveDate, 'x')
            .format('YYYY-MM-DD');
        initialValues = {
            ...apiConnection,
            EffectiveDate
        };
    }

    const apiConnectionOriginalValidations = (values: IFormValues) => validateApiConnection(values, initialValues, {
        actionType,
        audiencePrefix: AUDIENCE_PREFIX,
        apiConnections
    });

    return (
        <Grid
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Grid
                item
                sm={12}
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={async (values: IFormValues) => {
                        try {
                            await submitApiConnection(values, {
                                actionType,
                                audiencePrefix: AUDIENCE_PREFIX,
                                onClose,
                                setSubmitError
                            });
                        } catch (error) {
                            setSubmitError('Something went wrong');
                        }
                    }}
                    validate={apiConnectionOriginalValidations}
                    validateOnChange
                    validateOnMount
                >
                    {(formContentsProps: FormikProps<IFormValues>) => (
                        <FormContents
                            {...formContentsProps}
                            classes={classes}
                            formProps={formProps}
                            submitError={submitError}
                        />
                    )}
                </Formik>
            </Grid>
        </Grid>
    );
}